import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import ICatalog from "@models/ICatalog";
import ICatalog_Item from "@models/ICatalog_Item";
import { useAppContextNonNull } from "@contexts/AppContext";
import CatalogService from "@services/CatalogService";
import CatalogItemService from "@services/CatalogItemService";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";

type Props = {
    /** Catalog to search first, typically the serial's own catalog. Falls back to the first catalog. */
    defaultCatalogId?: number | null;
    /** The serial's current item: hidden from the results (re-SKU to the same item is rejected). */
    excludeCatalogItemId?: number | null;
    selectedItem: ICatalog_Item | null;
    onSelect: (item: ICatalog_Item | null) => void;
};

/**
 * Picks the finished product a serial is classified as (OR-1459): an active, serialized catalog
 * item. Components are left out (excludeComponents: the API hides them only when asked), since a
 * component is never a finished SKU. A catalog select appears only when the supplier has several.
 * Only the latest search's results are shown: a slower earlier response, or one for a catalog that
 * is no longer selected, is dropped.
 */
export const FinishedSkuPicker = (props: Props) => {
    const { defaultCatalogId, excludeCatalogItemId, selectedItem, onSelect } = props;
    const { userModel, supplierModel, vendorModel } = useAppContextNonNull();
    const intl = useIntl();

    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [catalogGuid, setCatalogGuid] = useState<string>("");
    const [options, setOptions] = useState<ICatalog_Item[]>([]);
    const [resetTypeahead, setResetTypeahead] = useState(false);
    const lookupRequestId = useRef(0);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const data = await CatalogService.getCatalogs(userModel, supplierModel, vendorModel);
                if (cancelled) return;

                const list = data.catalogs || [];
                setCatalogs(list);
                const initial = list.find((catalog) => catalog.id === defaultCatalogId) || list[0];
                setCatalogGuid(initial?.guid || "");
            } catch (error) {
                console.error("Error fetching catalogs:", error);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [userModel, supplierModel, vendorModel, defaultCatalogId]);

    const lookup = async (searchPhrase: string) => {
        if (!searchPhrase || !catalogGuid) return;

        const requestId = ++lookupRequestId.current;

        try {
            const data = await CatalogItemService.typeaheadCatalogItems(catalogGuid, searchPhrase, userModel, supplierModel, vendorModel, {
                onlyWithSerials: true,
                onlyActive: true,
                excludeComponents: true,
            });

            if (requestId !== lookupRequestId.current) return;

            setOptions((data.catalog_items || []).filter((item) => item.id !== excludeCatalogItemId));
        } catch (error) {
            console.error("Error searching catalog items:", error);
        }
    };

    const changeCatalog = (guid: string) => {
        // Invalidate any search still in flight for the previous catalog.
        lookupRequestId.current++;
        setCatalogGuid(guid);
        setOptions([]);
        onSelect(null);
        setResetTypeahead(true);
    };

    return (
        <div className="FinishedSkuPicker">
            {catalogs.length > 1 && (
                <select
                    className="form-select form-select-sm mb-2"
                    aria-label={intl.formatMessage({ id: "catalog", defaultMessage: "Catalog" })}
                    value={catalogGuid}
                    onChange={(e) => changeCatalog(e.target.value)}>
                    {catalogs.map((catalog) => (
                        <option key={catalog.guid} value={catalog.guid}>
                            {catalog.name}
                        </option>
                    ))}
                </select>
            )}
            <TypeaheadFn
                lookupFn={lookup}
                availableOptions={options}
                selectedOption={selectedItem}
                selectedOptionFn={(item: ICatalog_Item) => onSelect(item)}
                titleProp={"sku"}
                subTitleProp={"name"}
                placeholder={intl.formatMessage({ id: "search_by_sku_or_name", defaultMessage: "Search by SKU or Name" })}
                disabled={!catalogGuid}
                resetTypeahead={resetTypeahead}
                resetTypeaheadCallbackFn={() => setResetTypeahead(false)}
                clearSelectionFn={() => onSelect(null)}
                emitSelectionSetup={{ emitObject: true }}
                usePortal={true}
            />
        </div>
    );
};
